export const DEFAULT_USE_METRIC_UNITS = false;

// Must stay CONSTANT_CASE: these are the SpeedUnit / DistanceUnit enum values.
const METRIC_UNITS = { speedUnit: "KPH", distanceUnit: "KILOMETRE" };
const IMPERIAL_UNITS = { speedUnit: "MPH", distanceUnit: "MILE" };

const METRIC_INTL = { speed: "kilometer-per-hour", distance: "kilometer" };
const IMPERIAL_INTL = { speed: "mile-per-hour", distance: "mile" };

const METRIC_FALLBACK = { speed: "km/h", distance: "km" };
const IMPERIAL_FALLBACK = { speed: "mph", distance: "mi" };

// `??` not `||`: an explicit false is a real preference and must not fall through.
export const resolveUseMetricUnits = (userValue, tenantValue) =>
  userValue ?? tenantValue ?? DEFAULT_USE_METRIC_UNITS;

export const unitsFor = (useMetricUnits) =>
  useMetricUnits ? METRIC_UNITS : IMPERIAL_UNITS;

// `style: "unit"` is ES2020 and throws a RangeError on older engines.
const makeUnitFormatter = (locale, unit, fallbackLabel) => {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "unit",
      unit,
      maximumFractionDigits: 0,
    });
    return (value) => formatter.format(value);
  } catch (e) {
    const plain = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    return (value) => `${plain.format(value)} ${fallbackLabel}`;
  }
};

const UNIT_SUFFIX = /[\p{L}/]+$/u;

// Intl gives lowercase; the app shows units uppercase. "50 km/h" -> "50 KM/H".
const shout = (format) => (value) =>
  format(value).replace(UNIT_SUFFIX, (unit) => unit.toUpperCase());

// "50 KM/H" -> ["50 ", "KM/H"], so callers can style the unit on its own.
export const splitUnit = (formatted) => {
  const match = formatted.match(UNIT_SUFFIX);
  return match ? [formatted.slice(0, match.index), match[0]] : [formatted, ""];
};

export const makeFormatters = (useMetricUnits, locale) => {
  const intlUnits = useMetricUnits ? METRIC_INTL : IMPERIAL_INTL;
  const fallbacks = useMetricUnits ? METRIC_FALLBACK : IMPERIAL_FALLBACK;

  const speed = shout(
    makeUnitFormatter(locale, intlUnits.speed, fallbacks.speed),
  );
  const distance = shout(
    makeUnitFormatter(locale, intlUnits.distance, fallbacks.distance),
  );

  const guard = (format) => (value) =>
    value === null || value === undefined || Number.isNaN(value)
      ? ""
      : format(value);

  return {
    formatSpeed: guard(speed),
    formatDistance: guard(distance),
  };
};
